import {
  Button,
  Group,
  Input,
  InputWrapper,
  JsonInput,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { IconCheck, IconCode, IconMail, IconSend } from "@tabler/icons-react"
import { zodResolver } from "mantine-form-zod-resolver"
import { useEffect } from "react"

import { DashboardResponses } from "@costory/types/endpoints/dashboard"
import {
  DashboardDestinations,
  DashboardSchedulingDestinationArray,
  DashboardSchedulingForm,
  dashboardSchedulingFormSchema,
  ScheduleDashboardResponses,
  ScheduledDashboardConfigurationsRequests,
} from "@costory/types/endpoints/scheduledDashboardConfigurations"
import { ScheduledPeriod } from "@costory/types/prisma-client"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useDashboardListQuery } from "@costory/front/queries/dashboard"
import {
  useCreateScheduledDashboardConfigurationMutation,
  useEditScheduledDashboardConfigurationMutation,
  useTestScheduledDashboardConfigurationMutation,
} from "@costory/front/queries/scheduledDigestConfigurations"

interface _ConfigurationModalProps {
  dashboards: DashboardResponses.GetDashboard[]
  opened: boolean
  onClose: () => void
  scheduledDashboardConfiguration?: ScheduleDashboardResponses.List[0]
  handleSave: (values: ScheduledDashboardConfigurationsRequests.Create) => void
  initialDashboardId?: string
}
const exampleDestinationRecords: DashboardSchedulingDestinationArray = [
  {
    email: "example@email.com",
    templateValues: [
      {
        name: "Google Provider",
        value: [
          {
            field: "cos_provider",
            value: ["GCP"],
          },
        ],
      },
      {
        name: "AWS Provider",
        value: [
          {
            field: "cos_provider",
            value: ["AWS"],
          },
        ],
      },
    ],
  },
  {
    email: "team_gcp@email.com",
    templateValues: [
      {
        name: "Google Provider",
        value: [
          {
            field: "cos_provider",
            value: ["GCP"],
          },
        ],
      },
    ],
  },
]

const _ConfigurationModal = ({
  handleSave,
  dashboards,
  opened,
  onClose,
  scheduledDashboardConfiguration,
  initialDashboardId,
}: _ConfigurationModalProps) => {
  const form = useForm<DashboardSchedulingForm>({
    mode: "controlled",
    validateInputOnBlur: true,
    validateInputOnChange: true,
    initialValues: {
      dashboardId: initialDashboardId ?? "",
      destination: {
        type: DashboardDestinations.EMAIL,
        email: "",
      },
      scheduledPeriod: ScheduledPeriod.WEEKLY,
    },
    validate: zodResolver(dashboardSchedulingFormSchema),
  })
  const { mutateAsync: testConfiguration, isPending: isTesting } =
    useTestScheduledDashboardConfigurationMutation()

  useEffect(() => {
    if (scheduledDashboardConfiguration) {
      form.setValues({
        dashboardId: scheduledDashboardConfiguration.dashboardId,
        destination: {
          type: scheduledDashboardConfiguration.destination.type,
          email:
            "email" in scheduledDashboardConfiguration.destination
              ? scheduledDashboardConfiguration.destination.email
              : "",
          destinationRecords: JSON.stringify(
            scheduledDashboardConfiguration.destinationRecords.map(
              (record) => ({
                email: record.email,
                templateValues: record.templateValues,
              }),
            ),
          ),
        },
        scheduledPeriod: scheduledDashboardConfiguration.scheduledPeriod,
      })
    } else {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledDashboardConfiguration])

  const [
    jsonEditorModalOpen,
    { open: openJsonEditorModal, close: closeJsonEditorModal },
  ] = useDisclosure(false)
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        scheduledDashboardConfiguration
          ? "Edit scheduled dashboard"
          : "Add a scheduled dashboard"
      }
      size="lg"
    >
      <form
        onSubmit={() => {
          form.onSubmit(handleSave)()
          onClose()
        }}
      >
        <Stack gap="md">
          <InputWrapper label="Dashboard" required>
            <Select
              placeholder="Select a dashboard"
              searchable
              data={dashboards.map((dashboard) => ({
                value: dashboard.id,
                label: dashboard.name,
              }))}
              {...form.getInputProps("dashboardId")}
            />
          </InputWrapper>
          <InputWrapper label="Integration">
            <SegmentedControl
              color="primary"
              fullWidth
              data={[
                {
                  label: (
                    <Group justify="center">
                      <IconMail />
                      <Text>Email</Text>
                    </Group>
                  ),
                  value: DashboardDestinations.EMAIL,
                },
                {
                  label: (
                    <Group justify="center">
                      <IconCode />
                      <Text>Templated Destinations</Text>
                    </Group>
                  ),
                  value: DashboardDestinations.MULTI_EMAIL,
                },
              ]}
              {...form.getInputProps("destination.type")}
            />
          </InputWrapper>
          {form.getValues().destination.type ===
            DashboardDestinations.MULTI_EMAIL && (
            <Stack>
              <Group gap="xs">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => openJsonEditorModal()}
                >
                  Edit Destination Records (JSON)
                </Button>
              </Group>
              <Modal
                opened={jsonEditorModalOpen}
                onClose={closeJsonEditorModal}
                title="Edit Destination Records (JSON)"
                size="xl"
                centered
              >
                <Stack>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() =>
                      form.setFieldValue(
                        "destination.destinationRecords",
                        JSON.stringify(exampleDestinationRecords, null, 2),
                      )
                    }
                  >
                    Fill Example
                  </Button>
                </Stack>
                <JsonInput
                  label="Destination records"
                  formatOnBlur
                  autosize
                  minRows={12}
                  key={form.key("destination.destinationRecords")}
                  placeholder="Enter destination records"
                  {...form.getInputProps("destination.destinationRecords")}
                  validationError={
                    form.getInputProps("destination.destinationRecords").error
                  }
                />
                <Group justify="end" mt="md">
                  <Button onClick={() => closeJsonEditorModal()}>Done</Button>
                </Group>
              </Modal>
            </Stack>
          )}
          {form.getValues().destination.type ===
            DashboardDestinations.EMAIL && (
            <InputWrapper label="Email address">
              <Input
                placeholder="Enter email address"
                {...form.getInputProps("destination.email")}
              />
            </InputWrapper>
          )}

          <InputWrapper label="Frequency">
            <Stack gap={2}>
              <SegmentedControl
                color="primary"
                fullWidth
                data={[
                  { label: "Weekly", value: ScheduledPeriod.WEEKLY },
                  { label: "Monthly", value: ScheduledPeriod.MONTHLY },
                ]}
                {...form.getInputProps("scheduledPeriod")}
              />
              <Text size="xs" c="dimmed">
                The scheduled report will be sent{" "}
                {form.values.scheduledPeriod === ScheduledPeriod.WEEKLY
                  ? "every Tuesday"
                  : "every third day of the month"}
              </Text>
            </Stack>
          </InputWrapper>

          <Group justify="space-between">
            <Button
              type="button"
              leftSection={<IconSend />}
              onClick={() => testConfiguration(form.getValues())}
              disabled={!form.isValid() || isTesting}
              loading={isTesting}
            >
              Test
            </Button>
            <Button
              type="submit"
              leftSection={<IconCheck />}
              disabled={!form.isValid()}
            >
              {scheduledDashboardConfiguration ? "Save changes" : "Create"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}

export const EditScheduledDashboardConfigurationModal = ({
  opened,
  onClose,
  scheduledDashboardConfiguration,
  initialDashboardId,
}: {
  opened: boolean
  onClose: () => void
  scheduledDashboardConfiguration?: ScheduleDashboardResponses.List[0]
  initialDashboardId?: string
}) => {
  const query = useDashboardListQuery()
  const { mutateAsync: updateConfiguration } =
    useEditScheduledDashboardConfigurationMutation()
  const { mutateAsync: createConfiguration } =
    useCreateScheduledDashboardConfigurationMutation()
  const handleSave = (
    values: ScheduledDashboardConfigurationsRequests.Create,
  ) =>
    scheduledDashboardConfiguration
      ? updateConfiguration({
          ...values,
          id: scheduledDashboardConfiguration.id,
        })
      : createConfiguration(values)

  return (
    <QueryWrapper query={query}>
      {({ data }) => (
        <_ConfigurationModal
          dashboards={data}
          opened={opened}
          onClose={onClose}
          handleSave={handleSave}
          scheduledDashboardConfiguration={scheduledDashboardConfiguration}
          initialDashboardId={initialDashboardId}
        />
      )}
    </QueryWrapper>
  )
}
